import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import LayerMenu from "./LayerMenu"
import Search from "./search"
import { Spacer, Container } from "@bw/bits"
import { Topics, Flyers } from "@bw/layouts"
import Logo from "./logo"
import { breakpoints } from "../../../theme"

const HeaderWrapper = styled.div`
  position: fixed;
  margin-bottom: 1.45rem;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  color: #3a3a39;
  background-color: rgba(255, 255, 255, 1);
  transition: background-color 0.25s, color 0.25s;

  @media (min-width: ${breakpoints.small}px) {
    position: absolute;
    background-color: rgba(255, 255, 255, 0);
  }

  &.active {
    box-shadow: 10px 10px 35px rgba(0, 0, 0, 0.05);
    background: #fff;
    color: #000;
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: currentColor;
  font-weight: 300;
  font-size: 16px;
  line-height: 19px;
  transition: border-bottom 0.1s ease-in-out;
  border-bottom: 3px solid transparent;

  &:hover,
  &:active {
    border-color: var(--secondary);
    &:after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }
  }
`

const MobileLink = styled(StyledLink)`
  font-size: 25px;
  padding: 1.5em 0;
  font-weight: 400;
  border: 0;

  &.mobileShortcuts {
    font-size: 20px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid lightgray;
  }
  &.active {
    font-weight: 600;
  }
`

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Navigation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 15px;

  @media (min-width: ${breakpoints.small}px) {
    margin-top: 35px;
  }
`

const LogoWrapper = styled(Link)`
  color: currentColor;

  svg {
    margin: 10px 0 5px;
    height: auto;
    width: 97px;

    @media (min-width: ${breakpoints.small}px) {
      width: 75%;
    }

    @media (min-width: ${breakpoints.large}px) {
      height: unset;
      width: unset;
    }
  }
`

const TopMenu = styled.div`
  display: none;
  @media (min-width: ${breakpoints.medium}px) {
    display: flex;
    align-items: center;
    justify-content: end;
    color: #aaaaaa;
    margin-bottom: 15px;
  }

  > * + * {
    margin-left: 40px;
  }
`

const MainMenu = styled.div`
  display: none;
  @media (min-width: ${breakpoints.medium}px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  > * + * {
    margin-left: 40px;
  }
`

const activeStyle = {
  fontWeight: 700,
}

const Burger = styled.button`
  display: flex;
  align-items: center;
  justify-content: end;
  justify-self: end;
  width: 28px;
  padding: 0;
  background: none;
  border: 0;
  color: currentColor;
  cursor: pointer;

  > svg {
    > rect {
      transition: width 0.25s ease-in-out;
    }
  }
  &:hover {
    > svg {
      > rect:nth-child(1) {
        width: 20px;
      }
      > rect:nth-child(2) {
        width: 15px;
      }
      > rect:nth-child(3) {
        width: 28px;
      }
    }
  }
  @media (min-width: ${breakpoints.medium}px) {
    display: none;
  }
`

const ToggleTopics = styled.button`
  display: flex;
  flex-direction: column;
  font-family: "OneDirection";
  border: none;
  background: none;
  color: black;
  background-color: var(--secondary);
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  @media (min-width: ${breakpoints.small}px) {
    display: none;
  }

  &:hover {
    transform: scale(1.1);
    filter: brightness(110%);
  }
`

const Links = ({ mode, menu }) => {
  const MenuLink = mode === "desktop" ? StyledLink : MobileLink
  return menu.map((item, i) => (
    <MenuLink className={mode} key={i} to={item.to} activeStyle={activeStyle}>
      {item.label}
    </MenuLink>
  ))
}

const Header = ({ shortcuts, burger }) => {
  const [show, setShow] = React.useState(false)
  const [showTopics, setShowTopics] = React.useState(false)

  React.useEffect(() => {
    document.body.style.overflow = show ? "hidden" : null
  }, [show])

  return (
    <>
      <HeaderWrapper className="header">
        <Container>
          <Grid>
            <LogoWrapper to="/">
              <Logo />
            </LogoWrapper>
            <Navigation>
              <TopMenu>
                <Links mode="desktop" menu={shortcuts} />
                <Search />
                <Flyers />
              </TopMenu>
              <MainMenu>
                <Links mode="desktop" menu={burger} />
              </MainMenu>
              <Spacer direction="row">
                <ToggleTopics
                  onClick={() => {
                    setShowTopics(true)
                  }}
                >
                  Voir les sujets
                </ToggleTopics>

                <Burger
                  onClick={() => {
                    setShow(true)
                  }}
                >
                  <svg
                    width="26"
                    height="16"
                    viewBox="0 0 26 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect y="7" width="26" height="2" fill="currentColor" />
                    <rect width="26" height="2" fill="currentColor" />
                    <rect y="14" width="26" height="2" fill="currentColor" />
                  </svg>
                </Burger>
              </Spacer>
            </Navigation>
          </Grid>
        </Container>
      </HeaderWrapper>
      <LayerMenu show={show} setShow={setShow}>
        <Spacer direction="row">
          <Search />
          <Flyers />
        </Spacer>
        <Links mode="mobile" menu={burger} />
        <Links mode="mobileShortcuts" menu={shortcuts} />
      </LayerMenu>
      <LayerMenu show={showTopics} setShow={setShowTopics}>
        <Topics mobile />
      </LayerMenu>
    </>
  )
}

export { Header }
